/**
 * Compact, URL-path-safe encoding of an analysis request, used as the report id in shareable links:
 *   <reportCode>.<reportCode>~<fightFilter>~<encounterId36>.<encounterId36>~<minDuration36>
 * Fight filter: k = kills, e = encounters, a = everything, b<id36> = single boss.
 * Empty encounter list means every boss. Trailing empty fields are dropped.
 */
import { SpellUsageFightFilter } from '../types';

export interface TbcReportParams {
    reportCodes: string[];
    fightFilter: SpellUsageFightFilter;
    /** Empty = all bosses in the logs. */
    encounterIds: number[];
    minDurationSeconds: number;
}

const REPORT_CODE = /^[A-Za-z0-9]+$/;
const WCL_REPORT_BASE_URL = 'https://fresh.warcraftlogs.com/reports/';

export function reportCodeFromLogUrl(url: string): string | null {
    const match = url.match(/\/reports\/([A-Za-z0-9]+)/) || url.trim().match(/^([A-Za-z0-9]+)$/);
    return match ? match[1] : null;
}

export function logUrlFromReportCode(code: string): string {
    return `${WCL_REPORT_BASE_URL}${code}`;
}

function encodeFightFilter(filter: SpellUsageFightFilter): string {
    if (filter === 'kills') {
        return 'k';
    }
    if (filter === 'encounters') {
        return 'e';
    }
    if (filter === 'everything') {
        return 'a';
    }
    return `b${Number(filter.slice('boss:'.length)).toString(36)}`;
}

function decodeFightFilter(value: string): SpellUsageFightFilter | null {
    if (value === '' || value === 'k') {
        return 'kills';
    }
    if (value === 'e') {
        return 'encounters';
    }
    if (value === 'a') {
        return 'everything';
    }
    if (value.startsWith('b')) {
        const id = parseInt(value.slice(1), 36);
        return Number.isFinite(id) && id > 0 ? (`boss:${id}` as SpellUsageFightFilter) : null;
    }
    return null;
}

/** Null when a log URL has no recognisable report code. */
export function encodeReportParams(logUrls: string[], params: Omit<TbcReportParams, 'reportCodes'>): string | null {
    const codes = logUrls.map(reportCodeFromLogUrl);
    if (codes.length === 0 || codes.some((code) => code === null)) {
        return null;
    }
    const fields = [
        codes.join('.'),
        encodeFightFilter(params.fightFilter),
        params.encounterIds.map((id) => id.toString(36)).join('.'),
        params.minDurationSeconds > 0 ? Math.round(params.minDurationSeconds).toString(36) : '',
    ];
    while (fields.length > 1 && fields[fields.length - 1] === '') {
        fields.pop();
    }
    if (fields.length === 2 && fields[1] === 'k') {
        fields.pop();
    }
    return fields.join('~');
}

export function decodeReportParams(key: string): TbcReportParams | null {
    const [codesField = '', filterField = '', encountersField = '', minDurationField = ''] = key.split('~');
    const reportCodes = codesField.split('.').filter((code) => code.length > 0);
    if (reportCodes.length === 0 || !reportCodes.every((code) => REPORT_CODE.test(code))) {
        return null;
    }
    const fightFilter = decodeFightFilter(filterField);
    if (!fightFilter) {
        return null;
    }
    const encounterIds = encountersField
        .split('.')
        .filter((id) => id.length > 0)
        .map((id) => parseInt(id, 36));
    if (encounterIds.some((id) => !Number.isFinite(id) || id <= 0)) {
        return null;
    }
    const minDurationSeconds = minDurationField ? parseInt(minDurationField, 36) : 0;
    return {
        reportCodes,
        fightFilter,
        encounterIds,
        minDurationSeconds: Number.isFinite(minDurationSeconds) ? minDurationSeconds : 0,
    };
}
